import { i18n } from "@lingui/core";
import { button } from "@warp-ds/css/component-classes";
import WarpElement from "@warp-ds/elements-core";
import "@warp-ds/elements/components/card";
import { css, html } from "lit";
import { trackClickEvent } from "../tracking.js";
import { Brand, Subvertical } from "../../constants.js";
import { TrackingEventMap, eventReference } from "../../models/trackingEvent.js";
import { activateI18n } from "../../utils/i18n.js";
import { assertNever } from "../../utils/assertNever.js";

class SellMobilityVehicle extends WarpElement {
    declare brand: Brand;
    declare subvertical: Subvertical;
    declare trackingEventMap: TrackingEventMap;
    declare wrapperClass: string;

    static properties = {
        brand: { attribute: true, type: Brand },
        baseurl: { attribute: true, type: String },
        subvertical: { attribute: true, type: Subvertical },
        trackingEventMap: { type: Object },
        wrapperClass: { type: String },
    };
    static styles = [
        WarpElement.styles,
        css`
            @css-placeholder-uKFB6H;
        `,
    ];

    constructor() {
        super();
    }

    connectedCallback() {
        activateI18n();
        super.connectedCallback();
    }

    get newAdUrl() {
        const base = "/create-item/start/vertical/mobility";
        switch (this.subvertical) {
            case Subvertical.Car:
                return `${base}?expand=car`;
            case Subvertical.B2B:
                return `${base}?expand=agriculture`;
            case Subvertical.Boat:
                return `${base}?expand=boat`;
            case Subvertical.Mc:
                return `${base}?expand=mc`;
            case Subvertical.Caravan:
                return `${base}?expand=caravan`;
            default:
                return assertNever(this.subvertical, "Subvertical in newAdUrl");
        }
    }

    render() {
        const newAdUrl = this.newAdUrl;
        const nettbilUrl = `https://nettbil.no/finn?origin=car_browse`;

        const newAd = i18n.t({
            id: "market-frontpage.new-ad",
            message: "New ad",
        });

        const nettbil = i18n.t({
            id: "market-frontpage.nettbil",
            message: "Sell using Nettbil",
        });

        const wheelaway = i18n.t({
            id: "market-frontpage.wheelaway",
            message: "Sell using Wheelaway",
        });

        // The DEALER tracking event in tracking.ts hardcodes name "Sell car with Nettbil".
        // If this gate is widened (e.g. caravan opens up to Nettbil), rename the event there
        // and coordinate the chart filter update with Hanh Lai (Amplitude chart e-z6bjk8zm).
        const showExternalLink = this.brand === Brand.FINN && this.subvertical === Subvertical.Car;

        const externalLinkData = {
            url: this.brand === Brand.FINN ? nettbilUrl : "https://wheelaway.se",
            label: this.brand === Brand.FINN ? nettbil : wheelaway,
            evtReference: eventReference.DEALER,
        };

        return html`
            <div class="${this.wrapperClass || "space-x-8"}">
                ${
                    showExternalLink
                        ? html`<a
                              data-tracking-id=${this.brand === Brand.FINN ? "nettbil" : "wheelaway"}
                              @click=${() => trackClickEvent(this.trackingEventMap[externalLinkData.evtReference], externalLinkData.url)}
                              href=${externalLinkData.url}
                              class="${button.secondary} inline-flex px-16 items-center font-bold text-12 flex-1 hover:no-underline py-6"
                          >
                              ${externalLinkData.label}
                          </a>`
                        : ""
                }
                <a
                    data-tracking-id="new-ad"
                    @click=${() => trackClickEvent(this.trackingEventMap[eventReference.NEW_AD], newAdUrl)}
                    href=${newAdUrl}
                    class="${button.secondary} inline-flex px-16 font-bold items-center text-12 flex-1 hover:no-underline py-6"
                >
                    ${newAd}
                </a>
            </div>
        `;
    }
}

customElements.define("mfp-sell-mobility-vehicle", SellMobilityVehicle as unknown as CustomElementConstructor);
